import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useAuth } from '@/api/auth';
import { ApiError } from '@/api/client';

import '../market/market.css';
import './login.css';

interface FromLocation {
  pathname: string;
  search?: string;
}

/**
 * Достаём человеческое сообщение об ошибке входа.
 * 401 на /api/auth/login — это «неверный пароль», и текст приходит в теле
 * ответа (`detail`), а не в фиксированном message класса UnauthorizedError.
 * Поэтому сперва смотрим на detail сервера, потом на message, и лишь в конце
 * даём общий запасной текст.
 */
function loginErrorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    const detail = cause.detail as { detail?: unknown } | null;
    const serverMessage = detail?.detail;
    if (typeof serverMessage === 'string' && serverMessage) return serverMessage;
    if (cause.message) return cause.message;
  }
  return 'Не удалось войти';
}

export function LoginScreen() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const state = location.state as { from?: FromLocation; expired?: boolean } | null;
  const from = state?.from;
  const target = from ? `${from.pathname}${from.search ?? ''}` : '/';
  // Перехватчик 401 (истечение сессии) уводит сюда с флагом expired — показываем
  // отдельное сообщение, пока пользователь не начал вводить новый пароль.
  const expiredNotice = state?.expired && !error ? 'Сессия истекла — войдите снова' : null;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(password, email.trim() || undefined);
      navigate(target, { replace: true });
    } catch (cause) {
      setError(loginErrorMessage(cause));
      setBusy(false);
    }
  }

  return (
    <div className="market-page login-page">
      <section className="login-intro" aria-labelledby="login-title">
        <div className="login-brand" aria-label="TRAVEL CORTEX">
          <span>TRAVEL</span>
          <strong>CORTEX</strong>
          <em>market intelligence</em>
        </div>

        <div className="login-message">
          <span className="market-eyebrow">Станция наблюдения за рынком</span>
          <h1 id="login-title">Цена меняется. Решение должно быть доказуемым.</h1>
          <p>
            Travel Cortex соединяет предложения, историю цены и качество данных,
            чтобы отличать реальный срыв от случайного шума.
          </p>
        </div>

        <div className="login-capabilities" aria-label="Возможности станции">
          <article><i>01</i><strong>Каталог</strong><span>туры и авиабилеты в одном потоке</span></article>
          <article><i>02</i><strong>История</strong><span>каждое изменение цены во времени</span></article>
          <article><i>03</i><strong>Доказательства</strong><span>источники, свежесть и уверенность</span></article>
        </div>
      </section>

      <section className="login-access" aria-label="Вход в Travel Cortex">
        <form onSubmit={onSubmit} className="login-form">
          <div className="login-form-heading">
            <span className="market-eyebrow">Защищённый доступ</span>
            <h2>Войти в станцию</h2>
            <p>Участники входят по email и своему паролю. Владелец может оставить email пустым.</p>
          </div>

        {expiredNotice ? (
          <p role="status" className="login-notice">
            {expiredNotice}
          </p>
        ) : null}

          <label htmlFor="email" className="login-password">
            <span>Email участника (необязательно для владельца)</span>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
            />
          </label>

          <label htmlFor="password" className="login-password login-password-following">
            <span>Пароль</span>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Введите пароль"
            />
          </label>

        {error ? (
          <p role="alert" className="login-error">
            {error}
          </p>
        ) : null}

          <button type="submit" disabled={busy || password.length === 0}>
            <span>{busy ? 'Входим…' : 'Войти'}</span>
            <i aria-hidden>→</i>
          </button>

          <p className="login-security">Сессия хранится в защищённой cookie и не передаётся в адресной строке.</p>
        </form>
      </section>
    </div>
  );
}
